import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { QualiopiBadge } from './QualiopiBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Menu, X, User, LogOut, BookOpen, LayoutDashboard, ChevronDown, Sparkles, Phone, Mail, MapPin, Youtube, Send, Award, Clock, Instagram, Facebook, Globe } from 'lucide-react';
import { LanguagePicker } from './LanguagePicker';
import { AnnouncementBar } from './AnnouncementBar';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_730ca48e-6233-465b-bff6-fa986efd4fed/artifacts/gcnpy6sg_sacademie-logo-blanc-1024x547.png";

export const Navbar = () => {
  const { t } = useTranslation();
  const { lang } = useLangPrefix();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper: build a localized link path (preserves current language prefix)
  const L = (path) => withLangPrefix(path, lang);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => { logout(); navigate(L('/')); };

  const getDashboardLink = () => {
    if (!user) return L('/connexion');
    switch (user.role) {
      case 'admin': return L('/admin');
      case 'gestionnaire': return L('/gestion');
      case 'formateur': return L('/formateur');
      default: return L('/mes-formations');
    }
  };

  const navLinks = [
    { href: '/formations-sur-place', label: t('nav.courses') },
    { href: '/formations-en-ligne', label: t('nav.online'), badge: t('nav.online_badge') },
    { href: '/a-propos', label: t('nav.trainer') },
    { href: '/blog', label: t('nav.blog', 'Blog') },
    { href: '/faq', label: t('nav.faq') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const onHome = location.pathname === '/' || /^\/[a-z]{2}\/?$/.test(location.pathname);
  const onDarkPage = onHome || location.pathname.endsWith('/formations-en-ligne');
  const transparent = onDarkPage && !scrolled;

  return (
    <>
      <div className={`fixed top-0 left-0 right-0 z-[70] transition-all duration-500 ${
        scrolled ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      }`}>
        <AnnouncementBar />
      </div>
      {/* Top utility bar — desktop only, hides on scroll. z-[60] so the
          language picker dropdown overlays the header CTAs. Subtle bottom
          border + extra vertical padding create air with the main header. */}
      <div className={`hidden lg:block fixed top-[var(--announce-h,0px)] left-0 right-0 z-[60] bg-[#1F2A4A]/95 backdrop-blur-sm text-[#E8DCC8] border-b border-white/[0.07] transition-all duration-500 ${
        scrolled ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-11 flex items-center justify-between text-[11px] tracking-wide">
          <div className="flex items-center gap-5 text-white/65">
            <span className="flex items-center gap-2.5 text-[#C9B896]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9B896] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#C9B896]" />
              </span>
              <span className="text-white/75 font-light">{t('topbar.session_live')}</span>
            </span>
            <span className="w-px h-3 bg-white/12" />
            <a href="tel:+33465011066" className="flex items-center gap-2 hover:text-[#C9B896] transition-colors duration-300 font-light">
              <Phone className="w-3 h-3 stroke-[1.5]" /> +33 4 65 01 10 66
            </a>
            <span className="w-px h-3 bg-white/12" />
            <a href="mailto:assistance.sellerie@gmail.com" className="flex items-center gap-2 hover:text-[#C9B896] transition-colors duration-300 font-light">
              <Mail className="w-3 h-3 stroke-[1.5]" /> assistance.sellerie@gmail.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to={L('/artisan-pres-de-chez-vous')}
              className="group inline-flex items-center gap-2 rounded-full bg-[#C9B896] hover:bg-[#E0D4BA] text-[#1F2A4A] px-4 h-7 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors shadow-sm"
              data-testid="topbar-devis-cta"
            >
              <Sparkles className="w-3 h-3" strokeWidth={2.2} />
              {t('topbar.devis', 'Demande de devis')}
              <span className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all">→</span>
            </Link>
            <span className="w-px h-3 bg-white/12" />
            <div className="flex items-center gap-3 text-white/55">
              <a href="https://www.youtube.com/@sts.lasellerie-tout-simplement" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-[#C9B896] transition-colors duration-300" data-testid="social-youtube">
                <Youtube className="w-3.5 h-3.5 stroke-[1.5]" />
              </a>
              <a href="https://www.facebook.com/people/Sellerie-Academie/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#C9B896] transition-colors duration-300" data-testid="social-facebook">
                <Facebook className="w-3.5 h-3.5 stroke-[1.5]" />
              </a>
            </div>
            <span className="w-px h-3 bg-white/12" />
            <LanguagePicker variant="dark" />
          </div>
        </div>
      </div>

      <header className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'top-0 bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(31,42,74,0.12)] py-3'
          : `top-[var(--announce-h,0px)] lg:top-[calc(2.75rem_+_var(--announce-h,0px))] ${transparent ? 'bg-transparent' : 'bg-white/85 backdrop-blur-md'} py-5 lg:py-6`
      }`}>
        <nav className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            <Link to={L('/')} className="flex-shrink-0 relative z-10 flex items-center gap-3 group" data-testid="nav-logo">
              <img
                src={LOGO_URL}
                alt="Sellerie Académie"
                className={`h-10 md:h-12 w-auto transition-all duration-500 ${
                  transparent ? 'brightness-0 invert' : 'brightness-0'
                }`}
                style={{ filter: transparent ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(11%) sepia(15%) saturate(2510%) hue-rotate(190deg) brightness(95%) contrast(95%)' }}
              />
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const localized = L(link.href);
                const active = location.pathname === localized || location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={localized}
                    data-testid={`nav-${link.href.replace('/', '')}`}
                    className={`relative px-5 py-2.5 text-sm font-medium transition-all duration-300 rounded-full inline-flex items-center gap-1.5 group ${
                      link.highlight
                        ? 'bg-[#C9B896]/15 border border-[#C9B896]/60 text-[#1F2A4A] hover:bg-[#C9B896]/25'
                        : active
                        ? (transparent ? 'text-[#C9B896]' : 'text-[#1F2A4A]')
                        : (transparent ? 'text-white/85 hover:text-white' : 'text-[#1F2A4A]/75 hover:text-[#1F2A4A]')
                    }`}
                  >
                    {link.label}
                    {link.badge && (
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-[#C9B896] text-[#1F2A4A] px-1.5 py-0.5 rounded-full">
                        {link.badge}
                      </span>
                    )}
                    {/* Animated underline */}
                    <span className={`absolute left-5 right-5 -bottom-0.5 h-0.5 bg-[#C9B896] origin-left transition-transform duration-300 ${
                      active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`} />
                  </Link>
                );
              })}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`flex items-center gap-2 rounded-full px-4 ${transparent ? 'text-white hover:bg-white/10' : 'text-[#1F2A4A] hover:bg-[#1F2A4A]/5'}`}
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#C9B896]/30 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#C9B896]" />
                      </div>
                      <span className="font-medium">{user.first_name}</span>
                      <ChevronDown className="w-4 h-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-[#1F2A4A]/10 rounded-xl p-2 shadow-xl">
                    <DropdownMenuItem asChild className="rounded-lg hover:bg-[#C9B896]/15 cursor-pointer">
                      <Link to={getDashboardLink()} className="flex items-center gap-3 py-2" data-testid="nav-dashboard">
                        <LayoutDashboard className="w-4 h-4 text-[#C9B896]" />
                        <span className="text-[#1F2A4A]">{t('nav.dashboard', 'Tableau de bord')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-lg hover:bg-[#C9B896]/15 cursor-pointer">
                      <Link to={L('/mes-formations')} className="flex items-center gap-3 py-2" data-testid="nav-my-courses">
                        <BookOpen className="w-4 h-4 text-[#C9B896]" />
                        <span className="text-[#1F2A4A]">{t('nav.my_courses', 'Mes formations')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-lg hover:bg-[#C9B896]/15 cursor-pointer">
                      <Link to={L('/cours-en-ligne')} className="flex items-center gap-3 py-2" data-testid="nav-online-library">
                        <BookOpen className="w-4 h-4 text-[#C9B896]" />
                        <span className="text-[#1F2A4A]">{t('nav.online_library', 'Cours en ligne')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#1F2A4A]/10 my-2" />
                    <DropdownMenuItem onClick={handleLogout} className="rounded-lg hover:bg-red-500/10 text-red-600 cursor-pointer" data-testid="nav-logout">
                      <LogOut className="w-4 h-4 mr-3" /> {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to={L('/connexion')}>
                    <Button
                      variant="ghost"
                      className={`rounded-full px-4 inline-flex items-center gap-2 ${transparent ? 'text-white/85 hover:bg-white/10 hover:text-white' : 'text-[#1F2A4A]/85 hover:bg-[#1F2A4A]/5 hover:text-[#1F2A4A]'}`}
                      data-testid="nav-login"
                    >
                      <User className="w-4 h-4" />
                      <span className="hidden xl:inline">{t('nav.my_account')}</span>
                    </Button>
                  </Link>
                  <Link to={L('/formations-sur-place')}>
                    <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-medium px-6 rounded-full btn-premium shadow-lg shadow-[#C9B896]/20" data-testid="nav-register">
                      {t('nav.apply')} →
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile-only right cluster: quick signup CTA + burger */}
            <div className="lg:hidden flex items-center gap-2">
              <Link
                to={L('/formations-sur-place')}
                className="inline-flex items-center"
                data-testid="mobile-signup-cta"
              >
                <Button
                  size="sm"
                  className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-semibold px-4 h-9 rounded-full shadow-md shadow-[#C9B896]/30 text-xs tracking-wide"
                >
                  {t('nav.signup', "S'inscrire")}
                </Button>
              </Link>
              <button
                className={`p-2 transition-colors ${transparent ? 'text-white' : 'text-[#1F2A4A]'}`}
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="lg:hidden mt-4 pb-4 animate-fade-in bg-white rounded-2xl border border-[#1F2A4A]/10 p-4 shadow-xl">
              <div className="flex flex-col gap-1">
                {/* Quote request featured link (kept out of desktop navLinks) */}
                <Link
                  to={L('/artisan-pres-de-chez-vous')}
                  onClick={() => setMobileOpen(false)}
                  className="mb-2 flex items-center justify-between px-4 py-3 rounded-xl bg-[#C9B896]/20 text-[#1F2A4A] font-semibold text-sm"
                  data-testid="mobile-devis-link"
                >
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#C9B896]" strokeWidth={2} />
                    {t('topbar.devis', 'Demande de devis')}
                  </span>
                  <span className="text-[#C9B896]">→</span>
                </Link>
                {navLinks.map((link) => {
                  const localized = L(link.href);
                  return (
                    <Link
                      key={link.href}
                      to={localized}
                      onClick={() => setMobileOpen(false)}
                      className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                        location.pathname === localized ? 'text-[#1F2A4A] bg-[#C9B896]/20' : 'text-[#1F2A4A]/75 hover:bg-[#1F2A4A]/5'
                      }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
                <div className="border-t border-[#1F2A4A]/10 pt-3 mt-3 flex items-center justify-between">
                  <LanguagePicker variant="light" />
                </div>
                <div className="border-t border-[#1F2A4A]/10 pt-3 mt-3">
                  {user ? (
                    <>
                      <Link to={getDashboardLink()} onClick={() => setMobileOpen(false)} className="block px-4 py-3 rounded-xl text-[#1F2A4A]/75 hover:bg-[#1F2A4A]/5">{t('nav.dashboard', 'Tableau de bord')}</Link>
                      <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block w-full text-left px-4 py-3 rounded-xl text-red-600 hover:bg-red-50">{t('nav.logout')}</button>
                    </>
                  ) : (
                    <div className="flex flex-col gap-2 px-2">
                      <Link to={L('/connexion')} onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full border-[#1F2A4A]/20 text-[#1F2A4A] rounded-full">{t('nav.login')}</Button>
                      </Link>
                      <Link to={L('/formations-sur-place')} onClick={() => setMobileOpen(false)}>
                        <Button className="w-full bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full">{t('nav.apply')}</Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
};

export const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [visitInfo, setVisitInfo] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  useEffect(() => {
    let mounted = true;
    import('../lib/api').then(({ api }) => {
      api.getPublicVisitTotal().then((d) => { if (mounted) setVisitInfo(d); }).catch(() => {});
    });
    // Build version + timestamp — discreetly shown at the foot of the page.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`).then(r => r.json())
      .then((d) => { if (mounted) setVersionInfo(d); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const versionLine = (() => {
    if (!versionInfo) return null;
    let built = '';
    try {
      const d = new Date(versionInfo.built_at);
      built = d.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
      });
    } catch (_) {}
    return `Build ${versionInfo.version}${built ? ` · ${built}` : ''}`;
  })();

  const visitorsLine = (() => {
    if (!visitInfo || !visitInfo.total) return null;
    let since = '';
    try {
      since = new Date(visitInfo.since).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    } catch (_) {}
    const total = visitInfo.total.toLocaleString('fr-FR');
    return since ? `${total} visites depuis ${since}` : `${total} visites cumulées`;
  })();

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    toast.success('Merci ! Vous êtes inscrit·e à notre newsletter.');
    setNewsletterEmail('');
  };

  return (
    <footer className="bg-[#172239] text-[#E8DCC8] mt-auto">
      {/* Newsletter band */}
      <div className="border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-semibold text-[#C9B896] uppercase tracking-[0.3em] mb-2">Newsletter</p>
              <h4 className="text-2xl md:text-3xl font-light text-white leading-tight tracking-tight">
                Reçois les actus du métier et les ouvertures de sessions
              </h4>
            </div>
            <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C9B896]" />
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="ton@email.com"
                  className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/15 rounded-full text-white placeholder:text-white/40 focus:outline-none focus:border-[#C9B896]"
                  data-testid="footer-newsletter-email"
                />
              </div>
              <Button type="submit" className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-medium px-7 rounded-full whitespace-nowrap" data-testid="footer-newsletter-submit">
                <Send className="w-4 h-4 mr-2" /> S'abonner
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-5">
            <img src={LOGO_URL} alt="Sellerie Académie" className="h-14 w-auto mb-6" />
            <p className="text-[#E8DCC8]/70 text-sm leading-relaxed mb-6 max-w-md">
              Centre de formation en sellerie-garnissage à Istres. Formations tous publics
              de 3 à 23 jours, encadrées par <strong className="text-[#C9B896]">Benki de Gamenki</strong>,
              maître sellier-garnisseur formé chez les Compagnons du Devoir.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <a href="https://www.facebook.com/people/Sellerie-Academie/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#C9B896] hover:text-[#1F2A4A] flex items-center justify-center transition-all duration-300 group" data-testid="footer-social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@sts.lasellerie-tout-simplement" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#C9B896] hover:text-[#1F2A4A] flex items-center justify-center transition-all duration-300 group" data-testid="footer-social-youtube">
                <Youtube className="w-5 h-5" />
              </a>
            </div>

            {/* Membre du réseau STS — badge */}
            <Link
              to={L('/reseau-sts')}
              className="inline-flex items-center gap-3 bg-white/95 hover:bg-white border border-[#C9B896]/40 rounded-2xl pl-2.5 pr-5 py-2 transition-all hover:shadow-lg group"
              data-testid="footer-sts-badge"
              title="Découvrir le réseau STS — La Sellerie Tout Simplement"
            >
              <img
                src="/sa/STSS-removebg-preview-1.png"
                alt="Logo STS"
                className="h-10 w-auto shrink-0"
                loading="lazy"
              />
              <div className="leading-tight">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#C9B896]">Membre du réseau</p>
                <p className="text-[13px] font-semibold text-[#1F2A4A]">STS · La Sellerie Tout Simplement</p>
                <p className="text-[9px] uppercase tracking-wider text-[#1F2A4A]/60 mt-0.5 group-hover:text-[#1F2A4A]">Découvrir le réseau →</p>
              </div>
            </Link>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-3">
            <h4 className="text-[#C9B896] font-heading text-lg mb-6 flex items-center gap-2">
              <Award className="w-4 h-4" /> Formations
            </h4>
            <ul className="space-y-3 text-sm">
              <li><Link to={L('/formations-sur-place')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Sur place à Istres</Link></li>
              <li><Link to={L('/formations-en-ligne')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> E-learning <span className="text-[10px] text-[#C9B896] ml-1">(bientôt)</span></Link></li>
              <li><Link to={L('/a-propos')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Le formateur</Link></li>
              <li><Link to={L('/contact')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Nous contacter</Link></li>
              <li><Link to={L('/financements')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Financements</Link></li>
              <li><Link to={L('/certification-entrepreneur-sellerie-garnissage')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2" data-testid="footer-link-certification"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> La certification</Link></li>
              <li><Link to={L('/reseau-sts')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2" data-testid="footer-link-reseau-sts"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Le réseau STS</Link></li>
              <li><Link to={L('/blog')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Blog & savoir-faire</Link></li>
              <li><Link to={L('/sellerie-et-environnement')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2" data-testid="footer-link-impact"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Sellerie & environnement</Link></li>
              <li><Link to={L('/faq')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> FAQ</Link></li>
              <li><Link to={L('/devenir-apporteur')} className="text-[#E8DCC8]/70 hover:text-[#C9B896] transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B896]" /> Devenir apporteur d'affaires</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="text-[#C9B896] font-heading text-lg mb-6 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Nous trouver
            </h4>
            <ul className="space-y-4 text-sm text-[#E8DCC8]/70">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C9B896] mt-0.5 shrink-0" />
                <span>
                  25 Avenue du Tubé · ZAC du Tubé Sud<br />
                  Pépinière d'entreprises<br />
                  13800 Istres, France
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#C9B896] shrink-0" />
                <a href="tel:+33465011066" className="hover:text-[#C9B896] transition-colors">+33 4 65 01 10 66</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#C9B896] shrink-0" />
                <a href="mailto:assistance.sellerie@gmail.com" className="hover:text-[#C9B896] transition-colors break-all">assistance.sellerie@gmail.com</a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#C9B896] shrink-0" />
                <span>Lun. - Ven. · 9h - 18h</span>
              </li>
            </ul>

            {/* Qualiopi badge — clickable certificate (compact) */}
            <a
              href="/qualiopi/certificat-qualiopi-sellerie-academie.pdf"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-3 bg-white hover:bg-[#FAF7F0] border border-[#C9B896]/40 rounded-xl pl-3 pr-4 py-2 transition-all hover:shadow-md group"
              data-testid="footer-qualiopi-link"
              title="Télécharger le certificat Qualiopi (PDF)"
            >
              <QualiopiBadge size="sm" className="shrink-0" />
              <div className="leading-tight border-l border-[#1F2A4A]/15 pl-3">
                <p className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#1F2A4A]/65">N° QUA23050043</p>
                <p className="text-[11px] font-semibold text-[#1F2A4A]">Valide jusqu'au 10/07/2026</p>
                <p className="text-[8px] uppercase tracking-wider text-[#C9B896] mt-0.5 group-hover:text-[#1F2A4A]">Voir le certificat ↗</p>
              </div>
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="section-divider mt-14 mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#E8DCC8]/50">
            © {new Date().getFullYear()} Sellerie Académie · Tous droits réservés
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-[#E8DCC8]/50">
            <Link to={L('/mentions-legales')} className="hover:text-[#C9B896] transition-colors">Mentions légales</Link>
            <Link to={L('/cgv')} className="hover:text-[#C9B896] transition-colors">CGV</Link>
            <Link to={L('/confidentialite')} className="hover:text-[#C9B896] transition-colors">Confidentialité</Link>
            <Link to={L('/plan-du-site')} className="hover:text-[#C9B896] transition-colors" data-testid="footer-link-sitemap">Plan du site</Link>
          </div>
        </div>

        {visitorsLine && (
          <p className="mt-6 text-center text-[10px] tracking-[0.25em] uppercase text-[#E8DCC8]/30 font-mono" data-testid="footer-visit-counter">
            {visitorsLine}
          </p>
        )}

        {versionLine && (
          <p
            className="mt-2 text-center text-[10px] tracking-[0.2em] text-[#E8DCC8]/25 font-mono select-none"
            data-testid="footer-version"
            title="Identifiant et horodatage de la version actuellement en ligne"
          >
            {versionLine}
          </p>
        )}
      </div>
    </footer>
  );
};
